import { Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import { useCallback } from 'react'
import { Trans } from 'react-i18next'
import { RateRestriction, GuaranteeType } from 'src/api/api.generated'
import { ResourceRateInfo } from '../../form/reservation-new.type'

type ReservationChoiceRateInfo = {
  rateIsStopSell: boolean
  minStay?: number
  maxStay?: number
  restrictionsNotMet: RateRestriction[] | null | undefined
  bookingPolicyName: string
  policyPayment: { type: GuaranteeType; percentage?: number | null }
  closeToArrival?: string | null
  closeToDeparture?: string | null
  resourceRates: ResourceRateInfo[] | undefined
}

export default function ReservationChoiceRateInfo(props: ReservationChoiceRateInfo) {
  const { rateIsStopSell, minStay, maxStay, restrictionsNotMet, bookingPolicyName, policyPayment, closeToArrival, closeToDeparture, resourceRates } = props

  const restrictions = [...(restrictionsNotMet ?? []), ...(resourceRates?.flatMap((rate) => rate.restrictionsNotMet ?? []) ?? [])].filter((restriction) => {
    return restriction !== RateRestriction.STOPSELL_CHARGE
  })
  const uniqueRestrictions = [...new Set(restrictions)]

  const getRestrictionDescription = useCallback((restriction: RateRestriction) => {
    switch (restriction) {
      case RateRestriction.ADVANCE:
        return t('pms.booking.reservation_new.restriction_advance')
      case RateRestriction.CTA:
        return t('pms.booking.reservation_new.restriction_cta')
      case RateRestriction.CTD:
        return t('pms.booking.reservation_new.restriction_ctd')
      case RateRestriction.LASTMINUTE:
        return t('pms.booking.reservation_new.restriction_last_minute')
      case RateRestriction.MAXSTAY:
        return t('pms.booking.reservation_new.restriction_max_stay')
      case RateRestriction.MINSTAY:
        return t('pms.booking.reservation_new.restriction_min_stay')
      case RateRestriction.STOPSELL:
        return t('pms.booking.reservation_new.restriction_stopsell')
      case RateRestriction.STOPSELL_CHARGE:
        return t('pms.booking.reservation_new.restriction_stopsell_charge')
    }
  }, [])

  return (
    <ul
      style={{
        marginTop: 0,
        paddingLeft: 0,
        listStyle: 'none',
        lineHeight: 1,
      }}
    >
      {rateIsStopSell && (
        <li key="rateIsStopSell">
          <Stack direction="row" spacing={0.5}>
            <Typography variant="caption" color={'red'}>
              {t('pms.booking.reservation_new.rate_stop_sell')}
            </Typography>
          </Stack>
        </li>
      )}
      {minStay && (
        <li key="minStay">
          <Stack direction="row" spacing={1}>
            <Typography variant="caption">
              <Trans
                i18nKey="pms.booking.reservation_new.min_stay"
                values={{
                  minStay: minStay,
                }}
              />
            </Typography>
          </Stack>
        </li>
      )}
      {maxStay && (
        <li key="maxStay">
          <Stack direction="row" spacing={0}>
            <Typography variant="caption">
              {
                <Trans
                  i18nKey="pms.booking.reservation_new.max_stay"
                  values={{
                    maxStay: maxStay,
                  }}
                />
              }
            </Typography>
          </Stack>
        </li>
      )}
      <li key="bookingPolicyName">
        <Stack direction="row" alignItems={'center'}>
          <Typography variant="caption">
            {
              <Trans
                i18nKey="pms.booking.reservation_new.booking_policy_name"
                values={{
                  bookingPolicyName: bookingPolicyName,
                }}
              />
            }
          </Typography>
        </Stack>
      </li>

      <li key="bookingPolicy">
        <Stack direction="row" alignItems={'center'}>
          <Typography variant="caption">
            {policyPayment.type === 'NOTREQUIRED' && t('pms.booking.reservation_new.advance_not_required')}
            {policyPayment.type === 'FIRSTNIGHT' && t('pms.booking.reservation_new.advance_first_night')}
            {policyPayment.type === 'TOTAL' && t('pms.booking.reservation_new.advance_total')}
            {policyPayment.type === 'PERCENTAGE_TOTAL' && (
              <Trans
                i18nKey="pms.booking.reservation_new.advance_percentage"
                values={{
                  percentage: (policyPayment.percentage ?? 0) * 100,
                }}
              />
            )}
          </Typography>
        </Stack>
      </li>
      {closeToArrival && (
        <li key="closeToArrival">
          <Typography variant="caption">
            {
              <Trans
                i18nKey="pms.booking.reservation_new.close_to_arrival"
                values={{
                  cta: closeToArrival,
                }}
              />
            }
          </Typography>
        </li>
      )}
      {closeToDeparture && (
        <li key="closeToDeparture">
          <Typography variant="caption">
            {
              <Trans
                i18nKey="pms.booking.reservation_new.close_to_departure"
                values={{
                  ctd: closeToDeparture,
                }}
              />
            }
          </Typography>
        </li>
      )}

      {uniqueRestrictions?.map((restriction) => {
        return (
          <li key={restriction}>
            <Typography key={restriction} variant="caption" color="red">
              {getRestrictionDescription(restriction)}
            </Typography>
          </li>
        )
      })}
    </ul>
  )
}
